/**
 * Turning values into text. No fetching, no arithmetic, no state.
 *
 * Split out of `commands.ts` in M3/I2 so a setup can format without importing
 * the command grammar — which imports `api.ts`, which is the one thing a setup
 * may never reach. `test/setup-boundary.test.ts` walks the import graph and
 * fails the build if one does, and that test is only satisfiable because these
 * two functions live somewhere a setup can honestly depend on.
 */

/** Money arrives as a decimal string and stays one — grouped for reading only. */
export function formatUsd(decimal: string): string {
  const [whole = "0", frac = "0000"] = decimal.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${grouped}.${frac.slice(0, 2)}`;
}

/**
 * The day's move, signed, as a reader expects it: `+1.51%`, `-0.63%`, `0.00%`.
 *
 * The sign is always shown, including on a gain — a bare `1.51%` beside a price
 * reads as a quantity rather than a change, and the plus is what makes it a
 * change at a glance. Returns "" for no reference, so a caller can drop the
 * field entirely rather than print a made-up zero.
 */
export function formatChange(percent: number | undefined): string {
  if (percent === undefined) return "";
  const sign = percent > 0 ? "+" : percent < 0 ? "" : "";
  return `${sign}${percent.toFixed(2)}%`;
}

/**
 * Money that is routinely smaller than a cent: what a model call cost.
 *
 * **`formatUsd` truncates to two places and is wrong for a bill.** It is right
 * for a position, a limit or a balance, where a third decimal is noise — but an
 * agent run costs $0.000490, and rendering that as `$0.00` told a trader every
 * run was free. A spend surface built on it would have shown zero after
 * spending money, which is the one number on that screen anybody reads.
 *
 * **Six places under a dollar, cents at or above it**, and the threshold is a
 * dollar rather than a cent because two places is still a lie well past $0.01:
 * a first draft switched at a cent and rendered $0.014320 as `$0.01`, hiding a
 * third of the figure. Below a dollar every digit is signal here; above one,
 * the grouping is what makes a four-figure total readable.
 *
 * Zero is `0.000000`, not `0.00`. These print as a column, and one row in a
 * different shape from the rest is harder to read than a run of zeroes.
 */
export function formatSpendUsd(decimal: string): string {
  const [whole = "0", frac = ""] = decimal.split(".");
  if (whole.replace("-", "") !== "0") return formatUsd(decimal);
  const sign = whole.startsWith("-") ? "-" : "";
  return `${sign}0.${frac.slice(0, 6).padEnd(6, "0")}`;
}

/** Right-pad for column alignment without pulling in a formatting library. */
export const pad = (s: string, n: number) => s.padEnd(n, " ");

/**
 * An instant, in the VENUE's clock rather than the reader's.
 *
 * M4/E3: a US session printed in the viewer's timezone reads "opens 04:30 PM"
 * to anyone outside New York, which is not wrong so much as unusable — a trader
 * thinks in the venue's hours, and the response already carries `time_zone`
 * precisely so the terminal does not have to guess. Both the instant and the
 * zone come off the API; the only thing done here is rendering them together.
 *
 * The zone name is printed too (`4:00 PM EDT`), because a time in someone
 * else's timezone with no label is the bug this fixes wearing a different hat.
 *
 * An unknown zone falls back to the reader's own rather than throwing: a header
 * that renders nothing because a calendar named a zone this browser has never
 * heard of is worse than one that renders a time in the wrong frame and says so.
 */
export function venueTime(iso: string, timeZone: string): string {
  const at = new Date(iso);
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return at.toLocaleTimeString(undefined, { ...options, timeZone });
  } catch {
    return at.toLocaleTimeString(undefined, options);
  }
}

/** The same, with the day — for a transition that is not today. */
export function venueDateTime(iso: string, timeZone: string): string {
  const at = new Date(iso);
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  try {
    return `${at.toLocaleDateString(undefined, { ...options, timeZone })} ${venueTime(iso, timeZone)}`;
  } catch {
    return `${at.toLocaleDateString(undefined, options)} ${venueTime(iso, timeZone)}`;
  }
}

/**
 * What a venue is doing, as one phrase, in that venue's own clock.
 *
 * The shape is structural rather than `MarketSummary` so this file keeps its
 * one property: `test/setup-boundary.test.ts` walks the import graph, and the
 * reason `format.ts` exists at all is that a setup must be able to format
 * without reaching anything that reaches `api.ts`.
 *
 * **Every word comes off the response.** The state, the instant, the timezone
 * and the reason are all the server's; nothing here consults a calendar or a
 * clock to decide what a market is doing, which is PRODUCT.md's rule at the one
 * place it is most tempting to break — a browser has `Intl`, and a header that
 * works out the hours for itself looks right until the day it disagrees with
 * the engine that fills the orders.
 */
export function marketPhrase(m: {
  state: string;
  opens_at?: string | undefined;
  closes_at?: string | undefined;
  reason?: string | undefined;
  time_zone: string;
}): string {
  if (m.state === "open") {
    return m.closes_at ? `open · closes ${venueTime(m.closes_at, m.time_zone)}` : "open";
  }
  if (m.state === "closed") {
    const why = m.reason ? ` (${m.reason.replace(/_/g, " ")})` : "";
    return m.opens_at
      ? `closed${why} · opens ${venueDateTime(m.opens_at, m.time_zone)}`
      : `closed${why}`;
  }
  // Not "closed". `sessionAt` answers `unknown` past its calendar horizon, and
  // saying a venue is shut on the strength of a missing calendar entry is a
  // claim the server did not make. See M4/I4.
  return m.state;
}

/**
 * What is pricing you, in as few words as carry the fact.
 *
 * `alpaca (live)`, or the bare id when a build cannot name the kind. Both
 * halves travel because they answer different questions — *which* feed, and
 * whether these are real prices — and one without the other is either a name
 * that cannot explain itself or a claim that cannot be checked.
 *
 * **Naming the vendor here is deliberate.** `M4/I10` aliased it out of every
 * public response on the reasoning that which company sells the prices is a
 * commercial fact that changes without the product changing. That held while
 * the platform chose the feed; `M4/E30` inverted it when traders started
 * bringing their own, because a trader paying for their own entitlement is
 * entitled to see whose it is, and a picker that will not name the feeds cannot
 * be picked from. What survived is that a **refusal** never names a vendor —
 * `packages/registry/test/labels.test.ts` is the guard.
 *
 * Lifted out of `commands.ts` by `M4/E53`, when the banner needed the same
 * sentence the picker was already printing. Two ways of saying what prices you
 * is how a header and a command come to disagree.
 */
export function feedName(feed: { provider: string; kind: string | null } | undefined): string {
  if (!feed) return "an unknown feed";
  return feed.kind ? `${feed.provider} (${feed.kind})` : feed.provider;
}

/**
 * Why that feed is yours, said the way a trader would ask it.
 *
 * The banner has one job here and it is not completeness: a reader has to be
 * able to tell **"my key is working"** from **"it is not"** at a glance. Until
 * `M4/E53` the header said `US` in one case and `SIM` in the other and left the
 * mapping to be inferred, which is the half `M4/E52` did not fix.
 */
export function feedBecause(because: "everyone" | "your_key" | "your_plan"): string {
  if (because === "your_key") return "your key";
  if (because === "your_plan") return "your plan";
  return "everyone";
}
